import React, { useState, useEffect } from "react";
import pokemons from "./pokemonData";
import items from "./itemData";

const TeambuilderAdmin = () => {
  const [grid, setGrid] = useState(Array(4).fill().map(() => Array(5).fill(null)));
  const [extraPokemons, setExtraPokemons] = useState(Array(2).fill(null));
  const [extraTiles, setExtraTiles] = useState(Array(2).fill().map(() => Array(3).fill(null)));
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [teamcomps, setTeamcomps] = useState([]);
  const [selectedTeamcomp, setSelectedTeamcomp] = useState("");
  const [showItemSelection, setShowItemSelection] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetch("https://poke-meta.com/api/teamcomps")
      .then((res) => res.json())
      .then((data) => setTeamcomps(data))
      .catch((err) => console.error("Fehler beim Laden der Teamcomps:", err));
  }, []);

  useEffect(() => {
    if (!selectedTeamcomp) return;

    fetch(`https://poke-meta.com/api/teamcompdetails/${selectedTeamcomp}`)
      .then((res) => res.json())
      .then((data) => {
        // Grid setzen
        const newGrid = Array(4).fill().map(() => Array(5).fill(null));
        data.pokemon_positions.forEach((posObj) => {
          const pos = parseInt(posObj.pos) - 1;
          const row = Math.floor(pos / 5);
          const col = pos % 5;

          const match = pokemons.find(p => p.images.includes(posObj.image));
          if (match) {
            const stage = match.images.indexOf(posObj.image);
            newGrid[row][col] = { ...match, stage };
          }
        });
        setGrid(newGrid);

        // Extra Pokémon
        const newExtraPokemons = data.extra_pokemons.map((img) => {
          const match = pokemons.find(p => p.images.includes(img));
          if (!match) return null;
          const stage = match.images.indexOf(img);
          return { ...match, stage };
        });
        setExtraPokemons(newExtraPokemons);

        // Helditems
        const newExtraTiles = data.extra_tiles.map((row) =>
          row.map((item) => item === "None" ? null : item)
        );
        setExtraTiles(newExtraTiles);
      })
      .catch((err) => console.error("Fehler beim Laden der Teamcomp:", err));
  }, [selectedTeamcomp]);

  const handleSelectPokemon = (pokemon) => {
    setSelectedPokemon(pokemon);
  };

  const handleGridClick = (rowIndex, colIndex) => {
    const newGrid = grid.map((row) => [...row]);
    const cell = newGrid[rowIndex][colIndex];
    if (cell) {
      const nextStage = (cell.stage + 1) % cell.images.length;
      newGrid[rowIndex][colIndex] = { ...cell, stage: nextStage };
    } else if (selectedPokemon) {
      newGrid[rowIndex][colIndex] = { ...selectedPokemon, stage: 0 };
      setSelectedPokemon(null);
    }
    setGrid(newGrid);
  };

  const handleExtraPokemonClick = (index) => {
    const newExtras = [...extraPokemons];
    const cell = newExtras[index];
    if (cell) {
      const nextStage = (cell.stage + 1) % cell.images.length;
      newExtras[index] = { ...cell, stage: nextStage };
    } else if (selectedPokemon) {
      newExtras[index] = { ...selectedPokemon, stage: 0 };
      setSelectedPokemon(null);
    }
    setExtraPokemons(newExtras);
  };

  const handleSelectItem = (rowIndex, tileIndex, item) => {
    const newTiles = extraTiles.map((row) => [...row]);
    newTiles[rowIndex][tileIndex] = item.image;
    setExtraTiles(newTiles);
    setShowItemSelection(null);
  };

  const handleSaveTeam = async () => {
    if (!selectedTeamcomp) {
      alert("Bitte wähle eine Teamcomp aus!");
      return;
    }

    const payload = {
      teamcomp_id: selectedTeamcomp,
      pokemon_positions: grid
        .flatMap((row, rowIndex) =>
          row.map((cell, colIndex) =>
            cell ? { pos: rowIndex * 5 + colIndex + 1, image: cell.images[cell.stage] } : null
          )
        )
        .filter(Boolean),
      extra_pokemons: extraPokemons
        .map((p, index) => (p ? { slot: index + 1, image: p.images[p.stage] } : null))
        .filter(Boolean),
      extra_tiles: extraTiles.map((row, i) => ({
        pokemon_slot: i + 1,
        items: row.map((tile) => tile || "None"),
      })),
    };

    try {
      const res = await fetch("https://poke-meta.com/api/privatadmin/teambuilder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessMessage("✅ Team erfolgreich gespeichert!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        alert("Fehler: " + data.error);
      }
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      alert("Fehler beim Speichern. Bitte versuche es erneut.");
    }
  };

  return (
    <div className="teamcomps-container">
      <h1 className="teamcomps-title">Admin Teambuilder</h1>

      <div className="teamcomp-selection">
        <label>Teamcomp auswählen:</label>
        <select onChange={(e) => setSelectedTeamcomp(e.target.value)} value={selectedTeamcomp}>
          <option value="">-- Wähle eine Teamcomp --</option>
          {teamcomps.map((comp) => (
            <option key={comp.id} value={comp.id}>
              {comp.rating} - Teamcomp {comp.id}
            </option>
          ))}
        </select>
      </div>

      {/* Grid */}
      <div className="grid-wrapper">
        {grid.map((row, rowIndex) => (
          <div key={rowIndex} className={`grid-row ${rowIndex % 2 === 1 ? "offset" : ""}`}>
            {row.map((cell, colIndex) => (
              <div key={colIndex} className="grid-cell" onClick={() => handleGridClick(rowIndex, colIndex)}>
                {cell ? <img src={cell.images[cell.stage]} alt={cell.name} className="grid-pokemon" /> : null}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Extra Pokémon + Items */}
      <div className="extra-pokemon-box">
        <h2>Zusätzliche Pokémon:</h2>
        {extraPokemons.map((cell, index) => (
          <div key={index} className="extra-pokemon-row">
            <div className="extra-pokemon-slot" onClick={() => handleExtraPokemonClick(index)}>
              {cell ? <img src={cell.images[cell.stage]} alt={cell.name} className="grid-pokemon" /> : ""}
            </div>
            <div className="extra-tiles">
              {extraTiles[index].map((tile, tileIndex) => (
                <div key={tileIndex} className="extra-tile" onClick={() => setShowItemSelection({ rowIndex: index, tileIndex })}>
                  {tile ? <img src={tile} alt="Item" className="helditem" /> : ""}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Pokémon Auswahl */}
      <div className="pokemon-selection">
        <h2>Pokémon auswählen:</h2>
        <div className="pokemon-row">
          {pokemons.map((pokemon) => (
            <div key={pokemon.id} className="pokemon-slot" onClick={() => handleSelectPokemon(pokemon)}>
              <img src={pokemon.images[0]} alt={pokemon.name} className="pokemon-item" />
            </div>
          ))}
        </div>
      </div>

      {/* Speichern */}
      <button className="save-button" onClick={handleSaveTeam}>Speichern</button>
      {successMessage && <p className="success-message">{successMessage}</p>}

      {/* Helditem Modal */}
      {showItemSelection && (
        <div className="modal">
          <div className="modal-content">
            <h2>Helditem auswählen</h2>
            <div className="item-selection">
              {items.map((item) => (
                <img key={item.id} src={item.image} alt={`Item ${item.id}`} className="helditem-option"
                  onClick={() => handleSelectItem(showItemSelection.rowIndex, showItemSelection.tileIndex, item)}
                />
              ))}
            </div>
            <button onClick={() => setShowItemSelection(null)} className="close-button">Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeambuilderAdmin;
